import { useEffect, useMemo, useRef, useState } from "react";

import {
  LOOM_AUTHORING_ASSISTANT_MODEL,
  getLoomAuthoringAssistantStatus,
  proposeLoomAuthoringEdits,
  type LoomAuthoringAssistantProposal,
  type LoomAuthoringAssistantRequest,
  type LoomAuthoringAssistantStatus,
} from "../../api/loomAuthoringAssistant";
import {
  compileLoomControlEdits,
  deriveLoomParameterControls,
  formatLoomControlPath,
} from "./loomParameterControls";
import type {
  LoomControlWorkspaceIntent,
  LoomDesignGoal,
  LoomObservableDescriptor,
} from "./loomControlWorkspace";
import type {
  LoomAuthoringCommand,
  LoomAuthoringDocument,
} from "./loomAuthoringModel";
import { createLoomCommandId, type LoomCommandAcceptance } from "./loomAuthoringHistory";

interface LoomAuthoringChatProps {
  activeStateId: string;
  document: LoomAuthoringDocument;
  goal: LoomDesignGoal | null;
  onCommand: (command: LoomAuthoringCommand, acceptance?: LoomCommandAcceptance) => string | null;
  onWorkspaceIntent: (intent: LoomControlWorkspaceIntent) => string | null;
  observables: readonly LoomObservableDescriptor[];
  projectId: string;
  selectedNodeId: string;
}

type ChatStatus = "idle" | "asking" | "proposal" | "applied" | "error";
type ConnectionState = LoomAuthoringAssistantStatus | {
  state: "checking" | "thread-api-unreachable" | "thread-api-unauthorized";
  connected: false;
  model: typeof LOOM_AUTHORING_ASSISTANT_MODEL;
  reason: string | null;
  action: string | null;
};

interface AssistantContext {
  projectId: string;
  stateId: string;
  revision: number;
  selectedNodeId: string;
}

function sameContext(left: AssistantContext, right: AssistantContext): boolean {
  return left.projectId === right.projectId
    && left.stateId === right.stateId
    && left.revision === right.revision
    && left.selectedNodeId === right.selectedNodeId;
}

function displayError(error: unknown): string {
  const message = error instanceof Error ? error.message : "Unable to ask the Loom assistant";
  if (/not installed/i.test(message)) return `Required local model ${LOOM_AUTHORING_ASSISTANT_MODEL} is not installed.`;
  if (/Ollama.*(?:not reachable|unavailable)|API 503/i.test(message)) return "Local Ollama became unavailable. Check the connection status and retry.";
  if (/API 500|failed to fetch|network/i.test(message)) return "The local Thread API bridge is unavailable; Ollama was not contacted.";
  return message;
}

function connectionLabel(connection: ConnectionState): string {
  if (connection.state === "ready") return "connected";
  if (connection.state === "checking") return "checking";
  if (connection.state === "thread-api-unreachable") return "Thread API unavailable";
  if (connection.state === "thread-api-unauthorized") return "Thread API access required";
  if (connection.state === "model-missing") return "model missing";
  return "disconnected";
}

export function LoomAuthoringChat({
  activeStateId,
  document,
  goal,
  onCommand,
  onWorkspaceIntent,
  observables,
  projectId,
  selectedNodeId,
}: LoomAuthoringChatProps) {
  const [prompt, setPrompt] = useState("");
  const [proposal, setProposal] = useState<LoomAuthoringAssistantProposal | null>(null);
  const [proposalContext, setProposalContext] = useState<AssistantContext | null>(null);
  const [status, setStatus] = useState<ChatStatus>("idle");
  const [message, setMessage] = useState("Describe a change to the selected element.");
  const [connection, setConnection] = useState<ConnectionState>({
    state: "checking",
    connected: false,
    model: LOOM_AUTHORING_ASSISTANT_MODEL,
    reason: null,
    action: null,
  });
  const [connectionAttempt, setConnectionAttempt] = useState(0);
  const abortRef = useRef<AbortController | null>(null);
  const appliedRevisionRef = useRef<{ projectId: string; revision: number } | null>(null);
  const requestRef = useRef(0);
  const currentContextRef = useRef<AssistantContext>({
    projectId,
    stateId: activeStateId,
    revision: document.revision,
    selectedNodeId,
  });
  currentContextRef.current = { projectId, stateId: activeStateId, revision: document.revision, selectedNodeId };
  const controls = useMemo(
    () => deriveLoomParameterControls(document, selectedNodeId),
    [document, selectedNodeId],
  );
  const byId = useMemo(() => new Map(controls.map((control) => [control.id, control])), [controls]);

  useEffect(() => () => abortRef.current?.abort(), []);
  useEffect(() => {
    const controller = new AbortController();
    setConnection({
      state: "checking",
      connected: false,
      model: LOOM_AUTHORING_ASSISTANT_MODEL,
      reason: null,
      action: null,
    });
    void getLoomAuthoringAssistantStatus(projectId, controller.signal).then(
      (next) => { if (!controller.signal.aborted) setConnection(next); },
      (error: unknown) => {
        if (controller.signal.aborted) return;
        const unauthorized = /API (?:401|403)/.test(error instanceof Error ? error.message : "");
        setConnection({
          state: unauthorized ? "thread-api-unauthorized" : "thread-api-unreachable",
          connected: false,
          model: LOOM_AUTHORING_ASSISTANT_MODEL,
          reason: unauthorized
            ? "The Thread API rejected access to the local assistant status."
            : "The local Thread API bridge is not reachable, so Ollama has not been checked.",
          action: unauthorized
            ? "Sign in to Thread, then retry."
            : "Start the local Thread API (default port 8010), then retry.",
        });
      },
    );
    return () => controller.abort();
  }, [connectionAttempt, projectId]);
  useEffect(() => {
    abortRef.current?.abort();
    requestRef.current += 1;
    setProposal(null);
    setProposalContext(null);
    if (appliedRevisionRef.current?.projectId === projectId
      && appliedRevisionRef.current.revision === document.revision) {
      appliedRevisionRef.current = null;
      return;
    }
    setStatus("idle");
    setMessage("Describe a change to the selected element.");
  }, [activeStateId, document.revision, projectId, selectedNodeId]);

  const ask = async () => {
    const trimmed = prompt.trim();
    if (!trimmed || controls.length === 0) return;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const requestId = requestRef.current + 1;
    requestRef.current = requestId;
    const requestContext = currentContextRef.current;
    const request: LoomAuthoringAssistantRequest = {
      schema: "loom-authoring-assistant-request/v2",
      prompt: trimmed,
      expectedRevision: document.revision,
      selectedNodeId,
      controls: controls.map((control) => ({
        id: control.id,
        label: `${control.group} · ${control.label}`,
        unit: control.unit,
        value: control.value,
          ...(control.editor.kind !== "range" ? {} : {
          ...(control.editor.hardMin === undefined ? {} : { hardMin: control.editor.hardMin }),
          ...(control.editor.hardMax === undefined ? {} : { hardMax: control.editor.hardMax }),
          uiMin: control.editor.uiMin,
            uiMax: control.editor.uiMax,
          }),
          ...(control.editor.kind === "select" ? {
            allowedValues: control.editor.options.map((option) => option.value),
          } : {}),
        })),
      observableIds: observables.map((observable) => observable.id),
    };
    setProposal(null);
    setStatus("asking");
    setMessage("Local Ollama is preparing a reviewable parameter proposal…");
    try {
      const next = await proposeLoomAuthoringEdits(projectId, request, controller.signal);
      if (requestRef.current !== requestId || controller.signal.aborted
        || !sameContext(currentContextRef.current, requestContext)) return;
      if (next.focusControlIds.length > 0) {
        const error = onWorkspaceIntent({
          kind: "controls.pin",
          expectedRevision: next.expectedRevision,
          controlIds: next.focusControlIds,
        });
        if (error) throw new Error(error);
        setProposal(null);
        setStatus("applied");
        setMessage(next.message);
        return;
      }
      if (next.goal) {
        const error = onWorkspaceIntent({
          kind: "goal.define",
          expectedRevision: next.expectedRevision,
          variableControlId: next.goal.controlId,
          observableId: next.goal.observableId,
          targetValue: next.goal.targetValue,
          tolerance: next.goal.tolerance,
        });
        if (error) throw new Error(error);
      }
      setProposalContext(requestContext);
      setProposal(next);
      setStatus("proposal");
      setMessage(next.message || "Review the proposed parameter edits before applying them.");
    } catch (error) {
      if (requestRef.current !== requestId || controller.signal.aborted) return;
      setStatus("error");
      setMessage(displayError(error));
      setConnectionAttempt((current) => current + 1);
    }
  };

  const apply = () => {
    if (!proposal) return;
    if (!proposalContext || !sameContext(currentContextRef.current, proposalContext)
      || proposal.expectedRevision !== document.revision) {
      setStatus("error");
      setMessage(`Proposal is stale: it targets another Loom state or r${proposal.expectedRevision}, current definition is r${document.revision}.`);
      return;
    }
    try {
      const command = compileLoomControlEdits(document, proposal.edits, {
        commandId: createLoomCommandId("ollama-parameter", document.revision),
        expectedRevision: document.revision,
      });
      const error = onCommand(command, {
        origin: "assistant.ollama",
        subjectIds: proposal.edits.map((edit) => edit.controlId),
      });
      if (error) throw new Error(error);
      appliedRevisionRef.current = { projectId, revision: document.revision + 1 };
      setStatus("applied");
      setMessage(`Applied ${proposal.edits.length} reviewed edit${proposal.edits.length === 1 ? "" : "s"}; Loom extraction is now stale.`);
    } catch (error) {
      setStatus("error");
      setMessage(displayError(error));
    }
  };

  const stale = proposal !== null && (
    proposal.expectedRevision !== document.revision
    || proposalContext === null
    || !sameContext(currentContextRef.current, proposalContext)
  );
  const goalControl = goal ? byId.get(goal.variableControlId) : undefined;
  const goalObservable = goal ? observables.find((observable) => observable.id === goal.observableId) : undefined;
  return (
    <section className="loom-authoring-chat" data-testid="loom-authoring-chat">
      <div className="loom-chat-heading">
        <div><span>Local authoring copilot</span><strong>Ask → review → apply</strong></div>
        <code data-state={connection.state} data-testid="loom-chat-provider">
          Local Ollama · {proposal?.model ?? connection.model} · {connectionLabel(connection)}
        </code>
      </div>
      <div
        className={`loom-chat-connection is-${connection.state}`}
        data-testid="loom-chat-connection"
        role={connection.state === "checking" || connection.connected ? "status" : "alert"}
      >
        <span>{connection.state === "checking"
          ? "Checking the local Thread API and Ollama…"
          : connection.connected
            ? "Connected through the local Thread API."
            : connection.reason}</span>
        {connection.action ? <small>{connection.action}</small> : null}
        {!connection.connected && connection.state !== "checking" ? (
          <button
            data-testid="loom-chat-connection-retry"
            onClick={() => setConnectionAttempt((current) => current + 1)}
            type="button"
          >Retry local connection</button>
        ) : null}
      </div>
      <label>
        <span>Change {selectedNodeId}</span>
        <textarea
          aria-label="Ask local Ollama to edit the selected Loom element"
          data-testid="loom-chat-input"
          maxLength={2_000}
          onChange={(event) => setPrompt(event.target.value)}
          placeholder="e.g. Increase the wing sweep to 28° and reduce tip twist to −2°"
          value={prompt}
        />
      </label>
      <button
        aria-busy={status === "asking"}
        data-testid="loom-chat-submit"
        disabled={(connection.state !== "checking" && !connection.connected)
          || status === "asking" || prompt.trim() === "" || controls.length === 0}
        onClick={() => void ask()}
        type="button"
      >{status === "asking" ? "Asking local Ollama…" : "Propose parameter edits"}</button>
      <div
        className={`loom-chat-status is-${status}`}
        data-testid="loom-chat-status"
        role={status === "error" ? "alert" : "status"}
      >{message}</div>
      {proposal && proposal.edits.length > 0 ? (
        <div className="loom-chat-proposal" data-testid="loom-chat-proposal">
          <div><span>Proposal for definition r{proposal.expectedRevision}</span><strong>{proposal.edits.length} edit{proposal.edits.length === 1 ? "" : "s"}</strong></div>
          <ul>{proposal.edits.map((edit) => {
            const control = byId.get(edit.controlId);
            return <li key={edit.controlId}><span>{control?.label ?? edit.controlId}</span><code>{String(control?.value)} → {String(edit.value)} {control?.unit ?? ""}</code></li>;
          })}</ul>
          <button data-testid="loom-chat-apply" disabled={stale || status === "applied"} onClick={apply} type="button">
            {stale ? "Proposal is stale" : status === "applied" ? "Applied" : "Apply reviewed edits"}
          </button>
        </div>
      ) : null}
      {goal && goalControl && goalObservable ? (
        <article
          className="loom-goal-card"
          data-status={goal.status}
          data-target-control-path={formatLoomControlPath(goalControl.locator.path)}
          data-target-metric={goalObservable.label}
          data-target-value={goal.targetValue}
          data-testid="loom-goal-card"
        >
          <div><span>Design goal</span><strong>{goal.status}</strong></div>
          <p>Vary <b>{goalControl.label}</b> to reach <b>{goalObservable.label} = {goal.targetValue}</b> ± {goal.tolerance}.</p>
          <button
            aria-describedby={goal.status === "blocked" ? "loom-goal-blocker" : undefined}
            data-testid="loom-goal-run"
            disabled
            type="button"
          >Run goal search</button>
          {goal.status === "blocked" ? (
            <small data-testid="loom-goal-blocker" id="loom-goal-blocker">{goal.blockedReason}</small>
          ) : null}
        </article>
      ) : null}
      {controls.length === 0 ? <small>No schema-editable parameters are exposed for this node.</small> : (
        <small>Only the {controls.length} controls exposed by the selected Loom schema are sent. The model can focus controls or define an inert goal, but cannot execute or record anything.</small>
      )}
    </section>
  );
}
